import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SIMULATOR_URL } from "@/components/Nav";
import { track } from "@/lib/track";

const HERO_IMG =
  "https://images.unsplash.com/photo-1741277938635-dd700ca5fcde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const lineReveal = {
  hidden: { y: "115%" },
  show: (delay) => ({
    y: "0%",
    transition: { duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden" data-testid="hero">
      <motion.img
        src={HERO_IMG}
        alt=""
        aria-hidden="true"
        style={{ y, scale }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 50% at 18% 62%, rgba(61,107,255,0.16), transparent 70%), radial-gradient(40% 40% at 82% 18%, rgba(0,229,255,0.08), transparent 70%)",
        }}
      />

      <div className="container-x relative z-10 pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="eyebrow mb-8 text-beam"
          data-testid="hero-eyebrow"
        >
          QPI Laboratories, Inc. — Photonics · Simulation · Inference
        </motion.p>

        <h1 className="max-w-5xl text-[clamp(3.2rem,8.5vw,7.5rem)] font-extrabold leading-[0.95] tracking-tighter">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0.25} variants={lineReveal} initial="hidden" animate="show" className="block">
              Compute at
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0.4} variants={lineReveal} initial="hidden" animate="show" className="block">
              the speed
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              custom={0.55}
              variants={lineReveal}
              initial="hidden"
              animate="show"
              className="block text-laser"
              style={{ filter: "drop-shadow(0 0 24px rgba(61,107,255,0.5))" }}
            >
              of light.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 h-px w-40 origin-left bg-gradient-to-r from-laser to-transparent"
        />

        <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="max-w-xl"
          >
            <p className="text-lg font-light leading-relaxed text-zinc-300 md:text-xl">
              QPI Laboratories builds a simulator for photonic AI accelerators — where you
              find the compute sweet spot before it becomes silicon.
            </p>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base">
              QPI Labs provides the first publicly accessible simulator that runs real
              neural-network models on a photonic-compute abstraction. Developers can explore
              photonic matrix-vector multiplication, multi-wavelength compute, and photonic
              inference behavior inside full AI model graphs — long before hardware exists.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={SIMULATOR_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("simulator_launch")}
                data-testid="hero-cta-simulator"
                className="btn-laser"
              >
                Open the Simulator
                <span aria-hidden="true">↗</span>
              </a>
              <a href="/tutorial" data-testid="hero-cta-tutorial" className="btn-ghost">
                Take the Tutorial
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="hidden shrink-0 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 lg:block"
          >
            <div>λ — wavelength</div>
            <div className="mt-2">φ — phase shift</div>
            <div className="mt-2">N×N — mesh size</div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500" data-testid="hero-scroll-cue">
          Scroll
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="block h-8 w-px bg-gradient-to-b from-zinc-500 to-transparent"
          />
        </div>
      </motion.div>
    </section>
  );
}
